import React from 'react';

import clsx from 'clsx';

import { CheckIcon, HourglassIcon, UploadIcon } from 'components/Icons';
import Loader from 'components/Loader';
import { DocumentStatus } from 'enums/DocumentStatus';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './Dropzone.module.scss';

export interface DropzoneAreaProps {
  header: string;
  name: string;
  status: DocumentStatus;
  isDragActive: boolean;
  isDragReject: boolean;
  isAdditionalUploadAllowed?: boolean;
}

const UnsupportedFileFormat = 'Unsupported File Format';

const DropzoneArea = ({
  header,
  name,
  status,
  isDragActive,
  isDragReject,
  isAdditionalUploadAllowed = true,
}: DropzoneAreaProps) => {
  const loading = useAppSelector((state) => state.loading[`UPLOADING_${header}`]);

  const containerClassname = clsx({
    [styles.uploadStatus]: status === DocumentStatus.Upload,
    [styles.reviewStatus]: status === DocumentStatus.Pending,
    [styles.completedStatus]: status === DocumentStatus.Confirmed,
    [styles.droppableStatus]: isDragActive,
    [styles.rejectedStatus]: isDragActive && isDragReject && status !== DocumentStatus.Confirmed,
  });

  const renderIcon = () => {
    if (loading) {
      return <Loader size={24} containerWithoutMargin />;
    }

    if (isDragReject && status !== DocumentStatus.Confirmed) {
      return null;
    }

    switch (status) {
      case DocumentStatus.Upload:
        return <UploadIcon className={styles.uploadIcon} />;
      case DocumentStatus.Confirmed:
        return <CheckIcon className={styles.checkIcon} />;
      case DocumentStatus.Pending:
        return isAdditionalUploadAllowed ? (
          <div className={styles.hourglassContainer}>
            <HourglassIcon className={styles.hourGlassIcon} />
            Received
          </div>
        ) : (
          <HourglassIcon className={styles.hourGlassIcon} />
        );
      default:
        return null;
    }
  };

  const renderStatusMessage = () => {
    if ((loading || isDragReject) && status !== DocumentStatus.Confirmed) {
      return null;
    }

    switch (status) {
      case DocumentStatus.Upload:
        return <span className={styles.link}>Upload</span>;
      case DocumentStatus.Pending:
        return isAdditionalUploadAllowed ? (
          <span className={styles.link}>Upload Additional Files</span>
        ) : (
          <span>Received</span>
        );
      case DocumentStatus.Confirmed:
        return <span>Completed</span>;
      default:
        return null;
    }
  };

  return (
    <div className={containerClassname}>
      <p
        className={clsx(styles.titleContainer, {
          [styles.titleContainerDroppable]: isDragActive,
          [styles.titleContainerErrorFile]: isDragActive && isDragReject,
        })}
      >
        {isDragActive && isDragReject ? UnsupportedFileFormat : name}
      </p>
      <div className={styles.statusContainer}>
        {renderIcon()}
        {renderStatusMessage()}
      </div>
    </div>
  );
};

export default DropzoneArea;
